// «Потяни вниз, чтобы обновить» в нативных приложениях.
//
// Жест включается страницей, а не глобально: компонент объявляет опцию
//   pullToRefresh: true              — обновить = пересоздать текущий роут
//                                      (его mounted() заново грузит данные);
//   pullToRefresh() { return … }     — своя загрузка (promise), без ремаунта.
// Пока смонтирован хоть один такой компонент, жест активен. Сам жест и
// индикатор — один на всё приложение (components/common/PullToRefresh.vue).
//
// Ремаунт стирает несохранённое (черновик, правка на карточке), поэтому если
// пользователь на этом экране что-то вводил — сначала спрашиваем.
//
// Заменяет pulltorefreshjs: тот инициализировался в 52 компонентах
// и нигде не снимался — после первой же страницы со списком жест работал на
// любом экране, включая формы, и делал location.reload() всего приложения
// (заново бандл, сброс фильтров, прокрутки и введённого).

const targets = new Set();

export function registerPullTarget(vm) {
  targets.add(vm);
}

export function unregisterPullTarget(vm) {
  targets.delete(vm);
}

export function hasPullTargets() {
  return targets.size > 0;
}

// «Пользователь что-то вводил на этом экране». Событие input браузер шлёт
// только на ввод пользователя (набор, вставка, contenteditable) — программные
// присваивания v-model его не вызывают, поэтому заполненная с сервера форма
// чистая. Сбрасывается сменой роута и самим ремаунтом.
let dirty = false;

export function markUserInput(e) {
  if (e && e.isTrusted === false) return;
  dirty = true;
}

export function markClean() {
  dirty = false;
}

export function hasUserInput() {
  return dirty;
}

export const pullToRefreshMixin = {
  mounted() {
    if (this.$options.pullToRefresh) registerPullTarget(this);
  },
  beforeDestroy() {
    if (this.$options.pullToRefresh) unregisterPullTarget(this);
  },
};

// Открытые поверх страницы слои Vuetify 2: жест внутри них и под ними — не
// обновление страницы.
const OVERLAY_SELECTOR = [
  ".v-dialog__content",
  ".v-menu__content",
  ".v-navigation-drawer",
  ".v-overlay",
  "[data-no-pull-refresh]",
].join(", ");

function scrolledAncestor(el, win) {
  for (let node = el; node && node !== win.document.body; node = node.parentElement) {
    if (node.scrollTop > 0) {
      const { overflowY } = win.getComputedStyle(node);
      if (overflowY === "auto" || overflowY === "scroll") return true;
    }
  }
  return false;
}

// Можно ли начинать жест с этого касания. Только на самом верху документа,
// не внутри прокрученного контейнера (список чата, таблица) и не в
// открытом диалоге / меню / шторке.
export function canStartPull(target, win = window) {
  if (!hasPullTargets()) return false;
  if ((win.scrollY || win.pageYOffset || 0) > 0) return false;
  if (!target || typeof target.closest !== "function") return false;
  if (target.closest(OVERLAY_SELECTOR)) return false;
  // Детачнутые меню (v-menu, выпадашки v-select) оверлея не имеют: касание
  // под открытым меню до target.closest не доходит — проверяем глобально.
  if (win.document.querySelector(".v-dialog__content--active, .menuable__content__active")) {
    return false;
  }
  return !scrolledAncestor(target, win);
}

export const PULL_THRESHOLD = 72;
export const PULL_MAX = 120;

// Палец уходит дальше индикатора: сопротивление 0.5, потолок PULL_MAX.
export function pullDistance(dy) {
  if (dy <= 0) return 0;
  return Math.min(PULL_MAX, dy * 0.5);
}

// Что сделать по жесту. Цель — смонтированные компоненты с опцией (а не
// листовой компонент роута: на /teletype опция у вложенного TeletypeChat).
// Хоть у одного pullToRefresh: true — ремаунт роута (он заново грузит и
// вложенные), иначе — все их собственные загрузки. Перед ремаунтом, если был
// ввод, спрашиваем confirmDiscard() → Promise<boolean>.
export async function refreshPage({ remount, confirmDiscard }) {
  const list = Array.from(targets);
  if (list.some((vm) => vm.$options.pullToRefresh === true)) {
    if (hasUserInput() && !(await confirmDiscard())) return false;
    markClean();
    remount();
    return true;
  }
  await Promise.all(
    list.map((vm) => Promise.resolve(vm.$options.pullToRefresh.call(vm)).catch(() => {}))
  );
  return true;
}
